import type { OpenAPI } from "../../interfaces"
import SearchParams from "../searchParams"

type IIdeaImpactUpdate = Partial<OpenAPI.Schemas["IdeaImpact"]> & {
  authToken?: string
}

export const ideas = (api) => ({
  get(id: string, authToken: string | null, success: (data: OpenAPI.GET<"/ideas/{id}">["response"]) => void, fail) {
    api.maybeAuthenticatedRequest(
      "GET",
      `/ideas/${id}${authToken ? `?authToken=${authToken}` : ""}`,
      null,
      success,
      fail,
    )
  },
  update(id, data, success, fail) {
    api.maybeAuthenticatedRequest("PUT", `/ideas/${id}`, data, success, fail)
  },
  updateStatus(id, data, success, fail) {
    api.maybeAuthenticatedRequest("PUT", `/ideas/${id}/status`, data, success, fail)
  },
  assign(id, { authToken, authType }, success, fail) {
    api.authenticatedRequest("PUT", `/ideas/${id}/assign`, { authToken, authType }, success, fail)
  },
  delete(id, authToken, success, fail) {
    api.maybeAuthenticatedRequest(
      "DELETE",
      `/ideas/${id}${authToken ? `?authToken=${authToken}` : ""}`,
      null,
      success,
      fail,
    )
  },
  bulkDelete(ideaIds, success, fail) {
    api.authenticatedRequest("DELETE", "/ideas/bulk_delete", { ideas: ideaIds }, success, fail)
  },
  assigned(success, fail) {
    api.authenticatedRequest("GET", "/ideas/assigned", null, success, fail)
  },
  addCollaborator(id, userId, success, fail) {
    api.authenticatedRequest("PUT", `/ideas/${id}/members/${userId}`, null, success, fail)
  },
  addCollaboratorMulti(id, userIds, success, fail) {
    api.authenticatedRequest("PUT", `/ideas/${id}/members`, { users: userIds }, success, fail)
  },
  removeCollaborator(id, userId, success, fail) {
    api.authenticatedRequest("DELETE", `/ideas/${id}/members/${userId}`, null, success, fail)
  },
  updateOwner(id, userId, success, fail) {
    api.authenticatedRequest("PUT", `/ideas/${id}/owner/${userId}`, null, success, fail)
  },
  vote(id: string, add: boolean, success: (data: OpenAPI.POST<"/ideas/{id}/votes">["response"]) => void, fail) {
    api.authenticatedRequest(add ? "POST" : "DELETE", `/ideas/${id}/votes`, null, success, fail)
  },
  getComments(
    id: string,
    params: OpenAPI.GET<"/ideas/{id}/comments">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/ideas/{id}/comments">["response"]) => void,
    fail,
  ) {
    const query = new SearchParams(params).toSafeString()
    api.authenticatedRequest("GET", `/ideas/${id}/comments${query}`, null, success, fail)
  },
  postComment(id, data, success, fail) {
    api.authenticatedRequest("POST", `/ideas/${id}/comments`, data, success, fail)
  },
  deleteComment(id, commentId, success, fail) {
    api.authenticatedRequest("DELETE", `/ideas/${id}/comments/${commentId}`, null, success, fail)
  },
  pinComment(id, commentId, isPinning, success, fail) {
    api.authenticatedRequest("PUT", `/ideas/${id}/comments/${commentId}`, { isPinned: !!isPinning }, success, fail)
  },
  createAttachment(
    id: string,
    data: OpenAPI.POST<"/ideas/{id}/attachments">["requestBody"],
    success: (data: OpenAPI.POST<"/ideas/{id}/attachments">["response"]) => void,
    fail,
  ) {
    api.maybeAuthenticatedRequest("POST", `/ideas/${id}/attachments`, data, success, fail)
  },
  createMultipleAttachments(id, data: OpenAPI.POST<"/ideas/{id}/attachments/multiple">["requestBody"], success, fail) {
    api.maybeAuthenticatedRequest("POST", `/ideas/${id}/attachments/multiple`, data, success, fail)
  },
  deleteAttachment(
    id: string,
    uuid: string,
    authToken: string | undefined,
    success: (data: OpenAPI.DELETE<"/ideas/{id}/attachments/{uuid}">["response"]) => void,
    fail,
  ) {
    api.maybeAuthenticatedRequest(
      "DELETE",
      `/ideas/${id}/attachments/${uuid}${authToken ? `?authToken=${authToken}` : ""}`,
      null,
      success,
      fail,
    )
  },
  moveToChallenge(ideaIds, challengeId, success, fail) {
    const params = new SearchParams({
      ideas: ideaIds,
    })
    api.authenticatedRequest("PUT", `/ideas/challenges/${challengeId}${params.toSafeString()}`, null, success, fail)
  },
  getAssessment(
    id,
    assessmentRoundId,
    success: (data: OpenAPI.GET<"/ideas/{id}/assessment/{assessment_round_id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", `/ideas/${id}/assessment/${assessmentRoundId}`, null, success, fail)
  },
  updateAssessment(id, assessmentRoundId, data, success, fail) {
    api.authenticatedRequest("PUT", `/ideas/${id}/assessment/${assessmentRoundId}`, data, success, fail)
  },
  submitAssessment(id, assessmentRoundId, success, fail) {
    api.authenticatedRequest("POST", `/ideas/${id}/assessment/${assessmentRoundId}`, null, success, fail)
  },
  unsubmitAssessment(id, assessmentRoundId, userId, success, fail) {
    api.authenticatedRequest("DELETE", `/ideas/${id}/assessments/${assessmentRoundId}/${userId}`, null, success, fail)
  },
  commentAddReaction(id, commentId, emoji, success, fail) {
    api.authenticatedRequest("PUT", `/ideas/${id}/comments/${commentId}/reacts/${emoji}`, null, success, fail)
  },
  commentRemoveReaction(id, commentId, emoji, success, fail) {
    api.authenticatedRequest("DELETE", `/ideas/${id}/comments/${commentId}/reacts/${emoji}`, null, success, fail)
  },
  follow(id, success, fail) {
    api.authenticatedRequest("POST", `/ideas/${id}/followers`, null, success, fail)
  },
  unfollow(id, success, fail) {
    api.authenticatedRequest("DELETE", `/ideas/${id}/followers`, null, success, fail)
  },
  followers(id, params, success, fail) {
    const queryParams = new SearchParams(params).toString()
    api.authenticatedRequest("GET", `/ideas/${id}/followers?${queryParams}`, null, success, fail)
  },
  addImpactMeasure(id: string, impactId: string, data: IIdeaImpactUpdate, success, fail) {
    api.maybeAuthenticatedRequest("PUT", `/ideas/${id}/impact/${impactId}`, data, success, fail)
  },
  removeImpactMeasure(id: string, impactId: string, measureId: string, data: IIdeaImpactUpdate, success, fail) {
    api.maybeAuthenticatedRequest("DELETE", `/ideas/${id}/impact/${impactId}/measure/${measureId}`, data, success, fail)
  },
  updateImpactMeasure(id: string, impactId: string, measureId: string, data: IIdeaImpactUpdate, success, fail) {
    api.maybeAuthenticatedRequest("POST", `/ideas/${id}/impact/${impactId}/measure/${measureId}`, data, success, fail)
  },
  summarise(id, success, fail) {
    api.authenticatedRequest("POST", `/ideas/${id}/summarise`, null, success, fail)
  },
  merge(data, success, fail) {
    api.authenticatedRequest("POST", "/ideas/merge", data, success, fail)
  },
  updateMergedIdea(id, data, success, fail) {
    api.authenticatedRequest("POST", `/ideas/${id}/merge`, data, success, fail)
  },
  removeIdeaFromMerge(parentId, childId, success, fail) {
    api.authenticatedRequest("DELETE", `/ideas/${parentId}/merge/${childId}`, null, success, fail)
  },
  createIdeaNameWithAI(id, success, fail) {
    api.authenticatedRequest("GET", `/ideas/${id}/ai/name`, null, success, fail)
  },
  getCopySuggestions(ideaId, fieldType, params, success, fail) {
    const search = new SearchParams(params)
    api.authenticatedRequest(
      "GET",
      `/ideas/${ideaId}/copy/suggest/${fieldType}${search.toSafeString()}`,
      null,
      success,
      fail,
    )
  },
  copyData(fromIdea, fromField, toIdea, toField, params, success, fail) {
    const search = new SearchParams(params)
    api.authenticatedRequest(
      "GET",
      `/ideas/${fromIdea}/${fromField}/copyTo/${toIdea}/${toField}${search.toSafeString()}`,
      null,
      success,
      fail,
    )
  },
  setDeadline(data, success, fail) {
    api.authenticatedRequest("POST", `/ideas/deadline`, data, success, fail)
  },
  removeDeadline(ideaIds, success, fail) {
    api.authenticatedRequest("DELETE", `/ideas/deadline`, { ideaIds }, success, fail)
  },
  getImpactStatements(
    ideaId: string,
    authToken: string | undefined,
    success: (data: OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]) => void,
    fail,
  ) {
    const query = new SearchParams({ authToken }).toSafeString()
    api.maybeAuthenticatedRequest("GET", `/ideas/${ideaId}/impactStatement${query}`, null, success, fail)
  },
  getImpactStatement(
    ideaId: string,
    statementId: string,
    authToken: string | undefined,
    success: (data: OpenAPI.GET<"/ideas/{idea_id}/impactStatement/{statement_id}">["response"]) => void,
    fail,
  ) {
    const query = new SearchParams({ authToken }).toSafeString()
    api.maybeAuthenticatedRequest("GET", `/ideas/${ideaId}/impactStatement/${statementId}${query}`, null, success, fail)
  },
  updateImpactStatement(
    ideaId: string,
    statementId: string,
    data: OpenAPI.Schemas["ImpactStatement"],
    authToken: string | undefined,
    success: (data: OpenAPI.PUT<"/ideas/{idea_id}/impactStatement/{statement_id}">["response"]) => void,
    fail,
  ) {
    const query = new SearchParams({ authToken }).toSafeString()
    api.maybeAuthenticatedRequest("PUT", `/ideas/${ideaId}/impactStatement/${statementId}${query}`, data, success, fail)
  },
  createImpactStatement(
    ideaId: string,
    data: OpenAPI.Schemas["ImpactStatement"],
    authToken: string | undefined,
    success: (data: OpenAPI.POST<"/ideas/{idea_id}/impactStatement">["response"]) => void,
    fail,
  ) {
    const query = new SearchParams({ authToken }).toSafeString()
    api.authenticatedRequest("POST", `/ideas/${ideaId}/impactStatement${query}`, data, success, fail)
  },
  deleteImpactStatement(
    ideaId: string,
    statementId: string,
    success: (data: OpenAPI.DELETE<"/ideas/{idea_id}/impactStatement/{statement_id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("DELETE", `/ideas/${ideaId}/impactStatement/${statementId}`, null, success, fail)
  },
  getEmailLogs(
    ideaId: string,
    page: number,
    success: (data: OpenAPI.GET<"/ideas/{idea_id}/logs/email">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", `/ideas/${ideaId}/email/logs?page=${page}`, null, success, fail)
  },
  getEmailLog(
    ideaId: string,
    logId: string,
    success: (data: OpenAPI.GET<"/ideas/{idea_id}/logs/email/{log_id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", `/ideas/${ideaId}/email/logs/${logId}`, null, success, fail)
  },
})
